import { type ComponentType, useCallback, useEffect, useState } from 'react';

import { type IntlShape } from 'react-intl-next';

import { type ExtensionProvider } from '@atlaskit/editor-common/extensions';
import { type EditorActions } from '@atlaskit/editor-core';
import { cardsExtensionProvider } from '@atlassian/cards-extension';
import { carouselExtensionProvider } from '@atlassian/carousel-extension';
import {
	ExtensionMode,
	getHubDefaultParameters,
	type ProductDependenciesProviderProps,
} from '@atlassian/company-hub-common';
import { hubElementPlaceholderExtensionProvider } from '@atlassian/hub-element-placeholder';
import { linksMenuExtensionProvider } from '@atlassian/links-menu-extension';
import { sectionExtensionProvider } from '@atlassian/section-extension';
import { spotlightExtensionProvider } from '@atlassian/spotlight-extension';

import { getSectionExtensionContents } from '../../constants';

export interface HubExtensionProvidersProps {
	contentId: string;
	editorActions?: EditorActions;
	intl: IntlShape;
	openConfigPanel?: (localId: string) => void;
	PropsProvider: ComponentType<ProductDependenciesProviderProps>;
}

export const useHubExtensionProviders = (
	props: HubExtensionProvidersProps,
): ExtensionProvider<any>[] => {
	const { contentId, intl, PropsProvider, editorActions, openConfigPanel } = props;
	const [extensionProviders, setExtensionProviders] = useState<ExtensionProvider[]>([]);

	const loadProviders = useCallback(async () => {
		const hubExtensionProviders: ExtensionProvider[] = [];
		const defaultExtensionProviderProps = {
			contentId,
			propsProvider: PropsProvider,
			mode: ExtensionMode.EDITOR,
			defaultParameters: getHubDefaultParameters(),
		};

		const defaultExtensionInstanceProps = {
			editorActions,
			openConfigPanel,
			intl,
			contentId,
		};

		try {
			const DEFAULT_EXTENSION_PROVIDERS = [
				spotlightExtensionProvider,
				linksMenuExtensionProvider,
				cardsExtensionProvider,
				carouselExtensionProvider,
			];

			DEFAULT_EXTENSION_PROVIDERS.forEach(async (extensionProvider) => {
				const providerInstance = await extensionProvider(defaultExtensionProviderProps);
				const provider = await providerInstance(defaultExtensionInstanceProps);
				hubExtensionProviders.push(provider);
			});

			// section provider
			const sectionExtProviderInstance = await sectionExtensionProvider({
				...defaultExtensionProviderProps,
				getSectionExtensionContents,
			});
			const sectionExtProvider = await sectionExtProviderInstance(defaultExtensionInstanceProps);
			hubExtensionProviders.push(sectionExtProvider);

			const hubElementPlaceholderExtensionProviderInstance =
				await hubElementPlaceholderExtensionProvider(defaultExtensionInstanceProps);

			hubExtensionProviders.push(hubElementPlaceholderExtensionProviderInstance);

			setExtensionProviders(hubExtensionProviders);
		} catch (error) {
			// todo: log error to analytics
		}
	}, [editorActions, openConfigPanel, intl, PropsProvider, contentId]);

	useEffect(() => {
		loadProviders();
	}, [loadProviders]);

	return extensionProviders;
};
